import styled from 'styled-components';
import {
  Button,
  FormControl,
  FormError,
  FormLabel,
  Heading,
  Input,
} from '@alefeducation/ui-elements-library-core';
import React, { useState } from 'react';
import {
  CHECK_EMAIL,
  CONFIRM_PASSWORD,
  CONTACT_EMAIL,
  EMAIL_EXISTS,
  FIRST_NAME,
  GENERAL_INFORMATION,
  LAST_NAME,
  PASSWORD,
  PASSWORD_MISMATCH_ERROR_MESSAGE,
  WEAK_PASSWORD_ERROR_MESSAGE,
} from '../../utils/constants';
import { StyledTableSection } from '../CommonStyles/TableStyles';
import { isPasswordStrong } from '../../utils/Validators';
import { AdminGeneralInfo } from '../../pages/EditAdminUser/hooks/useGetAdminByIdState';
import InfoMessage from '../InfoMessage';

const StyledRow = styled.div`
  display: flex;
  column-gap: 24px;
`;

const StyledFormControl = styled(FormControl)`
  width: 40%;

  .check-email-btn {
    width: 100px;
  }
`;

const StyledInputAction = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 30px;
  gap: ${({ theme }: any) => `${theme.space[16]}px`};
`;

interface Props {
  admin: AdminGeneralInfo;
  shouldEmailDisabled?: boolean;
  disabled?: boolean;
  messageText?: string;
  isEmailChanged?: boolean;
  onCheckEmail?(): void;
  setIsEmailChanged?(status: boolean): void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const GeneralInformation: React.FC<Props> = (props: Props) => {
  const [hasPassError, setPassError] = useState<boolean>(false);
  const [isPasswordWeak, setWeakPassError] = useState<boolean>(false);

  const {
    admin: { email, firstName, lastName, password, confirmPassword },
    shouldEmailDisabled = false,
    disabled = false,
    isEmailChanged,
    messageText,
    onCheckEmail,
    onChange,
    setIsEmailChanged,
  } = props;

  const onEmailChange = (e: any) => {
    setIsEmailChanged?.(true);
    onChange(e);
  };

  return (
    <StyledTableSection>
      <Heading
        data-testid="general-info-heading"
        variant="heading-small"
        color={disabled ? 'primary.40' : 'text.100'}
      >
        {GENERAL_INFORMATION}
      </Heading>

      {!disabled && (
        <React.Fragment>
          <StyledRow data-testid="row-email">
            <StyledFormControl>
              <FormLabel data-testid="admin-email-label">
                {CONTACT_EMAIL}
              </FormLabel>
              <Input
                value={email}
                readOnly={shouldEmailDisabled}
                type="email"
                placeholder="Enter an email"
                disabled={shouldEmailDisabled}
                data-testid="admin-email"
                name={shouldEmailDisabled ? '' : 'email'}
                onChange={onEmailChange}
              />
            </StyledFormControl>
            <StyledFormControl>
              <StyledInputAction>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!isEmailChanged}
                  onClick={onCheckEmail}
                  className="check-email-btn"
                  data-testid={'check-email-button'}
                >
                  {CHECK_EMAIL}
                </Button>
                {messageText && <InfoMessage messageText={EMAIL_EXISTS} />}
              </StyledInputAction>
            </StyledFormControl>
          </StyledRow>

          <StyledRow data-testid="row-names">
            <StyledFormControl>
              <FormLabel data-testid="admin-firstname-label">
                {`${FIRST_NAME} *`}
              </FormLabel>
              <Input
                value={firstName}
                type="text"
                placeholder="Enter first name"
                data-testid="admin-firstname"
                name="firstName"
                onChange={onChange}
              />
            </StyledFormControl>
            <StyledFormControl>
              <FormLabel data-testid="admin-lastname-label">
                {`${LAST_NAME} *`}
              </FormLabel>
              <Input
                value={lastName}
                type="text"
                placeholder="Enter last name"
                data-testid="admin-lastname"
                name="lastName"
                onChange={onChange}
              />
            </StyledFormControl>
          </StyledRow>

          <StyledRow data-testid="row-passwords">
            <StyledFormControl
              appearance={isPasswordWeak ? 'error' : 'regular'}
            >
              <FormLabel data-testid="admin-password-label">
                {`${PASSWORD} *`}
              </FormLabel>
              <Input
                value={password}
                type="password"
                placeholder="Enter password"
                data-testid="admin-password"
                name="password"
                onChange={onChange}
                onBlur={() => {
                  if (password) {
                    setWeakPassError(!isPasswordStrong(password));
                  }
                }}
              />
              {isPasswordWeak && (
                <FormError data-testid={'weak-password'}>
                  {WEAK_PASSWORD_ERROR_MESSAGE}
                </FormError>
              )}
            </StyledFormControl>
            <StyledFormControl appearance={hasPassError ? 'error' : 'regular'}>
              <FormLabel data-testid="admin-confirm-password-label">
                {`${CONFIRM_PASSWORD} *`}
              </FormLabel>
              <Input
                value={confirmPassword}
                type="password"
                placeholder="Confirm Password"
                data-testid="admin-confirm-password"
                name="confirmPassword"
                onChange={onChange}
                onBlur={() => {
                  setPassError(confirmPassword !== password);
                }}
              />
              {hasPassError && (
                <FormError data-testid={'password-mismatch-error'}>
                  {PASSWORD_MISMATCH_ERROR_MESSAGE}
                </FormError>
              )}
            </StyledFormControl>
          </StyledRow>
        </React.Fragment>
      )}
    </StyledTableSection>
  );
};

export default GeneralInformation;
